import { API_STATUS } from 'core-lib/constants/network';
import { fetchAddressFromLatLng } from 'core-lib/services/fetchAddressFromLatLng';
import { AddressStatusType } from 'core-lib/types/Address';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Form from 'testDrive/components/Form';
import { LeadType, TestDriveVersion } from 'testDrive/constants';
import { useCheckServiceable } from 'testDrive/hooks/useCheckServiceable';
import { useFormDetails } from 'testDrive/hooks/useFormDetails';
import {
  updateDealer,
  updateFormDetails,
  updateLocation,
  updateServiceablePopup
} from 'testDrive/store';
import { ListType, UserDetailsType } from 'testDrive/types';
import { checkIfAllUserDetailsExist } from 'testDrive/utils';
import {
  Attributes,
  TestDriveEvents,
  trackTestDriveEvents
} from 'testDrive/utils/events';
import { AnimatedImage, BackButton, Button, Modal, Typography } from 'ui';
import Bookmark1 from 'ui/components/newIcons/Bookmark4';
import CloseCircle1 from 'ui/components/newIcons/CloseCircle1';
import Star from 'ui/components/newIcons/Star';
import Star1 from 'ui/components/newIcons/Star1';
import s from './BookingForm.module.scss';

interface BookingFormProps {
  data?: ListType;
  show: boolean;
  onClose: () => void;
  userDetails: UserDetailsType;
  pageName: string;
  address: any;
  list?: ListType[];
  replace?: boolean;
  updateData?: (args) => void;
  section: string;
  showServiceablePopup?: boolean;
  cityChange?: (city) => void;
  version?: string;
  modelText?: string;
}

let timeout;

const BookingForm = ({
  show,
  data,
  onClose,
  userDetails,
  pageName,
  address,
  list = [],
  replace = false,
  updateData = () => {},
  section,
  showServiceablePopup = true,
  cityChange = () => {},
  version = TestDriveVersion.v1,
  modelText = null,
}: BookingFormProps) => {
  const [showInstantBooking, setShowInstantBooking] = useState(false);
  const dealerDetails = useSelector(
    (state: any) => state?.testDrive?.dealerDetails
  );
  const { checkServiceable } = useCheckServiceable();

  const formDetails = useSelector(
    (state: any) => state?.testDrive?.formDetails
  );
  const dispatch = useDispatch();

  const {
    text,
    generateLead,
    generatingLead,
    handleChange,
    form,
    handleSubmit,
  } = useFormDetails({
    userDetails,
    pageName,
    address,
    data,
    list,
    replace,
    section,
    dealer: dealerDetails,
    version: version,
  });

  const handleCancel = async () => {
    if (timeout) {
      clearTimeout(timeout);
    }
    const response = await generateLead(true);
    if (response) {
      updateData(response);
      handleClose();
      handleChange('city', '');
      trackTestDriveEvents(TestDriveEvents.CancelBookingClicked, {
        [Attributes.PageName]: pageName,
      });
    }
  };

  const handleCityChange = async (location) => {
    const { locationData, error } = await fetchAddressFromLatLng({
      lat: location?.latitude,
      lng: location?.longitude,
      radius: 100,
    });
    let city: any = null;
    if (locationData) {
      city = {
        latitude: locationData?.lat,
        longitude: locationData?.lng,
        state: locationData?.state,
        locality: locationData?.locality,
        city: locationData?.city,
        pincode: locationData?.pincode,
        country: null,
        infraCity: locationData?.infraCity
          ? locationData?.infraCity
          : locationData?.city,
        zoneName: locationData?.zoneName
          ? locationData?.zoneName
          : locationData?.city,
        stateId: locationData?.stateId,
      };
    }
    if (city?.city && city?.latitude && city?.longitude) {
      dispatch(
        updateLocation({
          ...address,
          data: city,
          status: AddressStatusType.Success,
        })
      );
      if (city?.pincode) {
        handleChange('pincode', `${city.pincode}`);
      }
      if (city?.city) {
        handleChange('city', city.city);
      }
      if (!showServiceablePopup) {
        cityChange(city);
        handleClose();
      }
      const isServiceable = checkServiceable({
        city: city?.city,
        leadType: data?.leadType,
      });
      if (!isServiceable) {
        dispatch(updateFormDetails({ ...form }));
        if (showServiceablePopup) {
          dispatch(updateServiceablePopup(true));
        }
        cityChange(city);
        handleClose();
      }
    } else {
      trackTestDriveEvents('no_city', {
        ...location,
        ...error,
      });
    }
    handleChange('dealer_name', '');
    dispatch(updateDealer(null));
    trackTestDriveEvents('city_changed', {
      ...location,
    });
  };

  const showEmailInput = useMemo(() => {
    let isValid = false;

    if (data && data?.leadType === LeadType.Oem) {
      isValid = true;
    } else if (list?.length > 0) {
      const idx = list?.findIndex((item) => item?.leadType === LeadType.Oem);
      if (idx > -1) {
        isValid = true;
      }
    }
    return isValid;
  }, [data, list]);

  const showDealerInput = useMemo(() => {
    let isValid = false;

    if (data && data?.metaData?.fetchDealersList) {
      isValid = true;
    } else if (list?.length > 0) {
      const idx = list?.findIndex((item) => item?.metaData?.fetchDealersList);
      if (idx > -1) {
        isValid = true;
      }
    }
    return isValid;
  }, [data, list]);

  const handleClose = () => {
    clearTimeout(timeout);
    setShowInstantBooking(false);
    onClose();
  };

  useEffect(() => {
    const hasAllDetails = checkIfAllUserDetailsExist({
      list: list,
      userDetails: userDetails,
      address: address,
      data: data,
      formDetails: address?.data?.city ? formDetails : null,
    });

    if (show && hasAllDetails && address?.data) {
      setShowInstantBooking(true);
      timeout = setTimeout(() => {
        generateLead(false, true);
        clearTimeout(timeout);
      }, 3000);
    }
  }, [show]);

  useEffect(() => {
    return () => {
      handleClose();
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, []);

  return (
    <Modal
      show={show}
      head={{
        show: false,
        onClose: () => {
          handleClose();
        },
      }}
      noPadding
      rootStyle={{ zIndex: 3 }}
    >
      <>
        <BackButton
          onBack={() => {
            handleClose();
          }}
          type='hidden'
        />

        {showInstantBooking && data ? (
          <div className={s.carRoot}>
            <AnimatedImage
              src={data?.metaData?.bannerImage}
              className={s.image}
            />
            <button
              className={s.cross}
              onClick={() => {
                handleClose();
              }}
            >
              <CloseCircle1 primaryColor='grey_white' width={32} height={32} />
            </button>
            <div className={s.details}>
              <div className={s.carDetails}>
                <Typography
                  label={data?.displayModelName}
                  variant='h2'
                  style={{ paddingBottom: '8px' }}
                />
                {data?.displayPrice && (
                  <p>
                    <Typography
                      renderAs='SPAN'
                      label={data?.displayPrice}
                      variant='h3'
                    />
                    <Typography
                      renderAs='SPAN'
                      label=' onwards'
                      variant='body_para_m'
                      color='text_subheading'
                    />
                  </p>
                )}
                <div className={s.ratingContainer}>
                  <div className={s.stars}>
                    {Array(5)
                      .fill(1)
                      .map((item, idx) => (
                        <>
                          {idx + 1 <= Math.floor(data?.rating) ? (
                            <Star1
                              primaryColor='warning_60'
                              width={16}
                              height={16}
                            />
                          ) : (
                            <Star
                              primaryColor='text_label'
                              width={16}
                              height={16}
                            />
                          )}
                        </>
                      ))}
                  </div>
                  <Typography
                    label={`${data?.ratingCount} ratings`}
                    variant='body_para_r'
                    color='text_caption'
                  />
                </div>
              </div>
              <div className={s.imageWrapper}>
                <AnimatedImage
                  className={s.brandLogo}
                  src={data?.metaData?.brandLogo}
                />
              </div>
            </div>
            <div className={s.bookingContainer}>
              <div className={s.userDetails}>
                <Bookmark1 />
                <div>
                  <Typography label='Booking now' />
                  <Typography
                    label={text}
                    variant='body_para_r'
                    color='text_subheading'
                    style={{ textTransform: 'capitalize' }}
                  />
                </div>
              </div>
              {show && (
                <div className={s.progressWrapper}>
                  <div className={s.progress}></div>
                  <Button
                    label='Cancel'
                    variant='ButtonNegative'
                    size='Small'
                    onClick={() => {
                      handleCancel();
                    }}
                    isDisabled={[API_STATUS.in_progress].includes(
                      generatingLead
                    )}
                    isLoading={[API_STATUS.in_progress].includes(
                      generatingLead
                    )}
                    customStyle={{ minWidth: '69px' }}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className={s.formRoot}>
            <div className={s.header}>
              {modelText && (
                <Typography
                  className={s.modelText}
                  label={modelText}
                  variant='h4'
                />
              )}
              <button
                onClick={() => {
                  handleClose();
                }}
                className={s.cross}
              >
                <CloseCircle1 width={32} height={32} primaryColor='grey_50' />
              </button>
            </div>
            <Form
              handleChange={(name, value) => {
                handleChange(name, value);
              }}
              data={data}
              form={form}
              handleCityChange={(location) => {
                handleCityChange(location);
              }}
              showDealerInput={showDealerInput}
              showEmailInput={showEmailInput}
              address={address}
            />
            <div className={s.footer}>
              <Button
                label='Request a callback'
                onClick={(e) => {
                  handleSubmit(e);
                  dispatch(updateFormDetails({ ...form }));
                }}
                fullwidth
                isDisabled={[API_STATUS.in_progress].includes(generatingLead)}
                isLoading={[API_STATUS.in_progress].includes(generatingLead)}
              />
              <Typography
                label='Don’t worry! We won’t spam you'
                variant='small_m'
                color='text_subheading'
                style={{
                  textAlign: 'center',
                  marginTop: '16px',
                  padding: '8px 0px',
                }}
              />
            </div>
          </div>
        )}
      </>
    </Modal>
  );
};

export default BookingForm;
